import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { NavLink, useLocation } from "react-router-dom"
import { PATHS } from "../routes/paths"
import { ModuleId, getModuleDefinition } from "../config/moduleRegistry"
import { useFacilityAccess } from "../lib/FacilityAccessContext"
import {
  Package,
  Skull,
  BellRing,
  NotebookText,
  Banknote,
  ShieldCheck,
  Receipt,
  Activity,
  ShieldAlert,
  Gauge,
  TrafficCone,
  AlertTriangle,
  Download,
  Megaphone,
  Menu,
  X,
  ChevronDown,
  Search as SearchIcon,
  User,
  Plane,
  Bus,
  Car,
  Boxes,
  Building2,
  Landmark,
  Bug,
  FileBadge2,
  ClipboardList,
  FileCheck2,
  Stethoscope,
  FileText,
  Radiation,
  ClipboardCheck,
  IdCard,
  History,
  Droplets,
  Sparkles,
  TrainFront,
  BookOpen,
  Users2,
  SlidersHorizontal,
  Bell,
  MapPinned,
  Book,
  History as HistoryIcon,
  ArrowUpRight,
  CheckCircle2,
  Trash2,
  CircleDashed,
  BarChart3,
  Home as HomeIcon,
  GitPullRequest,
  CircleHelp,
  ThermometerSun,
  Warehouse,
  FileSearch,
  QrCode,
} from "lucide-react"
import Swal from "sweetalert2"
import MenubarQrScannerModal from "./MenubarQrScannerModal"

type RouteKey = keyof typeof PATHS
type RoutePath = (typeof PATHS)[RouteKey]

type LinkItem = { label: string; to: RoutePath; icon?: React.ReactNode }
type SectionHelpLink = { label: string; location: string; description?: string }
type SectionHelp = {
  title?: string
  processFlow?: string | string[]
  items: SectionHelpLink[]
}

type SectionLayout = {
  columns: number
  columnLabels?: string[][]
}

type Section = { title: string; links: LinkItem[]; help?: SectionHelp; layout?: SectionLayout }

type DropdownGroup = {
  id: string
  label: string
  badge?: string | number
  icon?: React.ReactNode
  type: "dropdown"
  sections: Section[]
}

type LinkGroup = {
  id: string
  label: string
  badge?: string | number
  icon?: React.ReactNode
  type: "link"
  link: LinkItem
}

type Group = DropdownGroup | LinkGroup

type MenuLinkConfig = {
  moduleId: ModuleId
  label?: string
  icon?: React.ReactNode
  route?: keyof typeof PATHS
}

type MenuSectionConfig = {
  title: string
  links: MenuLinkConfig[]
  help?: SectionHelp
  layout?: SectionLayout
}

type MenuGroupDropdownConfig = {
  id: string
  label: string
  icon?: React.ReactNode
  badge?: string | number
  sections: MenuSectionConfig[]
  type?: "dropdown"
}

type MenuGroupLinkConfig = {
  id: string
  label: string
  icon?: React.ReactNode
  badge?: string | number
  type: "link"
  link: MenuLinkConfig
}

type MenuGroupConfig = MenuGroupDropdownConfig | MenuGroupLinkConfig

const ADMIN_ROLE_KEYWORDS = ["NAT", "NATIONAL"]
const COLLAPSED_ADMIN_ROLE_CODES = new Set(["NAT", "NATIONALADMIN"])
const ADMIN_GROUP_ID = "administration"

// NAT variants (e.g., "NAT Admin", "National Admin") should see the administration menu just like the bare code.
function canAccessAdministration(roleCode?: string | null): boolean {
  if (!roleCode) return false
  const normalized = roleCode.trim().toUpperCase()
  if (!normalized) return false

  const compact = normalized.replace(/[^A-Z0-9]/g, "")
  if (COLLAPSED_ADMIN_ROLE_CODES.has(normalized) || COLLAPSED_ADMIN_ROLE_CODES.has(compact)) {
    return true
  }

  const tokens = normalized.split(/[^A-Z0-9]+/).filter(Boolean)
  if (tokens.length === 0) tokens.push(normalized)

  return tokens.some((token) => ADMIN_ROLE_KEYWORDS.includes(token))
}

export const MENUBAR_GROUPS: MenuGroupConfig[] = [
  {
    id: "trav",
    label: "Traveller",
    icon: <User className="w-4 h-4" />,
    sections: [
      {
        title: "Traveller Health Control",
        help: {
          title: "Traveller Health Control SOPs",
          processFlow: [
            "Review submitted THDFs to identify travellers requiring border health attention.",
            "Conduct screening and triage assessments for flagged arrivals and capture outcomes.",
            "Create surveillance cases for travellers needing follow-up monitoring.",
            "Verify yellow fever vaccination status before clearing or escalating the traveller.",
          ],
           items: [
            {
              label: "Traveller Health Declaration Forms",
 
              location: "Traveller Health Control SOP · Chapter 4 · Intake Verification Checklist (pp. 18–22)",
              description: "Step-by-step actions for receiving, authenticating, and filing Traveller Health Declaration Forms including specimen handling notes.",
            },
            {
               label: "Screening & Triage",
              location: "Traveller Screening BRD · Section 3.2 · Primary & Secondary Screening Workflow (pp. 12–15)",
              description: "Interview prompts, vitals capture requirements, and referral criteria for moving a traveller through the triage pathway.",
            },
            {
              label: "Primary Screening",
              location: "Traveller Health Control SOP · Annex B · Documentation Prompts for High-Risk Origins (pp. 26–29)",
              description:
                "Record temperature checks, conveyance details, and automatically prompt Yellow Fever or other documentation based on the traveller's country.",
            },
            {

              label: "Sick Traveller Escalation",
              location: "Sick Traveller Escalation BRD · Section 2.3 · Isolation & Reporting Flow (pp. 7–9)",
              description: "Decision tree for triage, communication routing, and approvals when illness triggers an escalation at the point of entry.",
            },
             {
              label: "Surveillance Cases",
              location: "Post-entry Surveillance SOP · Section 5 · Monitoring & Follow-up Actions (pp. 21–25)",
              description: "Guidance for opening, updating, and closing surveillance cases plus documentation expectations for follow-up visits.",
            },
            {
              label: "Yellow Fever Checks",
              location: "Yellow Fever Verification SOP · Annex C · Certificate Review Standards (pp. 32–34)",
              description: "Authenticity checks, waiver handling, and escalation instructions before clearing the traveller to proceed.",
            },
 
            {
              label: "Dead on Arrival (DoA) Cases",
              location: "Human Remains Incident BRD · Section 4 · DoA Response Workflow (pp. 19–23)",
              description:
                "Notification chain, temporary storage management, and reporting requirements for DoA investigations.",
            },
          ],
        },
        links: [
           {
            moduleId: "traveller-primary-screening",
            label: "Primary Screening",
            icon: <ThermometerSun className="w-4 h-4" />,
            route: "TRAVELLER_PRIMARY_SCREENING_LIST",
          },
          {
            moduleId: "traveller-thdf",
            label: "Traveller Health Declaration",
            icon: <ClipboardCheck className="w-4 h-4" />,
            route: "THDF_CASES",
          },
          {
            moduleId: "hr-doa-cases",
            label: "Dead on Arrival",
            icon: <Skull className="w-4 h-4" />,
            route: "HR_DOA_CASES_LIST",
          },

          // {
          //   moduleId: "traveller-surveillance",
          //   label: "Surveillance Cases",
          //   icon: <ClipboardList className="w-4 h-4" />,
          //   route: "SURVEILLANCE_CASES_LIST",
          // },
         
          // {
          //   moduleId: "traveller-screening",
          //   label: "Yellow Fever Checks",
          //   icon: <IdCard className="w-4 h-4" />,
          //   route: "TRAVELLER_YF_LIST",
          // },
        ],
      },
      {
        title: "Passenger Health Management",
        links: [
          {
            moduleId: "traveller-ill-passenger",
            label: "Ill Traveller Reports",
            icon: <FileText className="w-4 h-4" />,
            route: "PASSENGER_ILL_REPORTS_LIST",
          },
          {
            moduleId: "outbreak-contact-tracing",
            label: "Contact Tracing",
            icon: <MapPinned className="w-4 h-4" />,
            route: "OUTBREAK_CONTACT_TRACING",
          },
          // {
          //   moduleId: "traveller-screening",
          //   label: "Screening & Triage",
          //   icon: <Stethoscope className="w-4 h-4" />,
          //   route: "TRAVELLERS_SCREENING_LIST",
          // },
        ],
      },
    ],
  },
  {
    id: "conv",
    label: "Conveyance",
    icon: <Plane className="w-4 h-4" />,
    sections: [
      {
        title: "Conveyance Oversight",
        help: {
          title: "Conveyance Oversight References",
          processFlow: [
            "Check arriving conveyance notifications and risk flags to plan inspection coverage and staffing.",
            "Coordinate pre-arrival briefings with operators and review sanitation certificate history.",
            "Execute on-board or ground inspections, documenting findings and corrective actions in PHMIS.",
            "Issue clearances or escalate for enforcement based on inspection outcomes and compliance status.",
          ],
          items: [
            {
              label: "Aircraft Inspections",
              location: "Aircraft Inspection SOP · Section 3.1–3.4 · On-board Inspection Tasks (pp. 11–19)",
              description: "Pre-arrival coordination, cabin walkthrough sequencing, and post-flight corrective action recording guidance.",
            },
            {
              label: "General Declaration",
              location: "Port Entry Processing SOP · Section 2.1 · Manifest & General Declaration Verification (pp. 8–10)",
              description: "Data validation steps and compliance checks required before logging an arrival declaration in PHMIS.",
            },
            {
              label: "Vehicle General Declaration",
              location: "Ground Transport Entry SOP · Section 2 · Vehicle Declaration Intake (pp. 6–9)",
              description: "Intake checklist for capturing vehicle arrivals, crew details, and inland contact information.",
            },
            {
              label: "Bus Inspections",
              location: "Ground Transport Inspection SOP · Section 3 · Coach & Truck Inspection Protocol (pp. 14–19)",
              description: "Inspection sequences, safety checks, and documentation specific to buses and cargo trucks at land borders.",
            },
          ],
        },
        links: [
          {
            moduleId: "conveyance-general",
            label: "General Declaration",
            icon: <Plane className="w-4 h-4" />,
            route: "GENERAL_DECLARATIONS",
          },
          {
            moduleId: "conveyance-vehicle-general",
            label: "Vehicle General Declaration",
            icon: <Car className="w-4 h-4" />,
            route: "VEHICLE_GENERAL_DECLARATIONS_LIST",
          },
        
          {
            moduleId: "conveyance-road",
            label: "Bus Inspections",
            icon: <Bus className="w-4 h-4" />,
            route: "CONVEYANCE_ROAD_INSPECTIONS_LIST",
          },
          {
            moduleId: "conveyance-passenger-train-inspections",
            label: "Passenger Train Inspections",
            icon: <TrainFront className="w-4 h-4" />,
            route: "PASSENGER_TRAIN_INSPECTIONS_LIST",
          },
        ],
      },
      {
        title: "Checklists & Certificates",
        help: {
          title: "Checklist & Certificate Guides",
          processFlow: [
            "Pick the checklist or certificate workflow that matches the conveyance type and risk profile.",
            "Capture findings, deficiencies, and corrective actions directly within PHMIS checklist forms.",
            "Issue or update certificates once compliance evidence is validated and approvals are recorded.",
            "Track expiry dates and follow-up actions so renewals and escalations happen on schedule.",
          ],
          items: [
            {
              label: "Hazardous Cargo Checklists",
              location: "Health Inspection Checklist · Commercial Trucks & Hazardous Cargo (context library)",
              description:
                "Form-driven assessment covering containment, labeling, documentation, and emergency readiness for hazardous consignments.",
            },
            {
              label: "Bus Inspection Checklists",
              location: "Ground Transport Inspection SOP · Annex C · Checklist Completion Standards (pp. 24–27)",
              description: "Stepwise prompts for vehicle safety equipment, passenger amenities, and driver documentation validation.",
            },
            {
              label: "Conveyance Checklists",
              location: "Conveyance Inspection Toolkit · Section 3 · Standardized Hygiene Assessments (pp. 12–16)",
              description: "Guidance on selecting vessel, road, or rail templates and recording control measure outcomes in PHMIS.",
            },
            {
              label: "Sanitation Certificates",
              location: "Conveyance Sanitation Certificates BRD · Appendix B · Issuance Criteria Matrix (pp. 32–34)",
              description: "Business rules governing initial issuance, renewal thresholds, and revocation triggers for sanitation certificates.",
            },
          ],
        },
        links: [
            {
            moduleId: "conveyance-aircraft",
            label: "Aircraft Inspections",
            icon: <Plane className="w-4 h-4" />,
            route: "CONVEYANCES_AIRCRAFT",
          },
          {
            moduleId: "conveyance-vehicle-hazard",
            label: "Hazardous Cargo Checklists",
            icon: <ShieldAlert className="w-4 h-4" />,
            route: "VEHICLE_HAZARD_CHECKLISTS_LIST",
          },
          {
            moduleId: "conveyance-road",
            label: "Bus Inspection Checklists",
            icon: <ClipboardList className="w-4 h-4" />,
            route: "CONVEYANCE_BUS_CHECKLISTS_LIST",
          },
        
          {
            moduleId: "conveyance-sanitation",
            label: "Sanitation Certificates",
            icon: <Sparkles className="w-4 h-4" />,
            route: "SANITATION_CERTS",
          },
        ],
      },
     
    ],
  },
  {
    id: "cargo",
    label: "Cargo",
    icon: <Boxes className="w-4 h-4" />,
    sections: [
      {
        title: "Cargo Inspections",
        help: {
          title: "Cargo Inspection Playbook",
          processFlow: [
            "Capture the advance manifest and register consignments slated for inspection or sampling.",
            "Assess hazardous classifications, PPE requirements, and inspection resources before opening cargo.",
            "Verify regulatory certificates, record inspection findings, and flag consignments for lab analysis where needed.",
            "Close out inspections with clearance decisions or enforcement actions and update certificate registers.",
          ],
          items: [
            {
              label: "Register Consignment",
              location: "Cargo Inspection SOP · Section 2.1–2.4 · Consignment Intake & Scheduling (pp. 6–12)",
              description: "Manifest validation steps, inspection prioritisation, and queue management for arriving consignments.",
            },
            {
              label: "Hazardous Substances",
              location: "Hazardous Cargo Handling SOP · Section 4 · Risk Classification & PPE Controls (pp. 18–23)",
              description: "Classification matrix, containment precautions, and notification requirements for dangerous goods inspections.",
            },
            {
              label: "Certificates",
              location: "Cargo Clearance BRD · Annex B · Certificate Types & Issuance Rules (pp. 34–38)",
              description: "Guidance on when to issue, renew, or revoke cargo health certificates and supporting documentation checks.",
            },
            {
              label: "Detention Certificates",
              location: "Cargo Detention SOP · Section 4 · Issuing & Releasing Detention Orders (pp. 28–32)",
              description: "Criteria for detaining consignments, approval workflow, and clearance once remedial actions are verified.",
            },
            {
              label: "Sampling & Lab Submissions",
              location: "Laboratory Sampling SOP · Section 3 · Sample Collection & Chain-of-Custody (pp. 11–16)",
              description: "Sampling criteria, packaging standards, and custody logging before dispatching samples to accredited labs.",
            },
          ],
        },
        links: [
          {
            moduleId: "cargo-inspections",
            label: "Register Consignment",
            icon: <ClipboardList className="w-4 h-4" />,
            route: "INSPECTIONS_CARGO",
          },
      
       
         
          {
            moduleId: "cargo-warehouse-inspections",
            label: "Warehouse Goods  Inspections",
            icon: <Warehouse className="w-4 h-4" />,
            route: "CARGO_WAREHOUSE_INSPECTIONS_LIST",
          },
         
        
          
          // {
          //   moduleId: "vector-tyres",
          //   label: "Used Tyres Inspection",
          //   icon: <CircleDashed className="w-4 h-4" />,
          //   route: "VECTOR_TYRES_LIST",
          // },
        ],
      },
      {
        title: "Reports & Certificates",
        links: [
              {
            moduleId: "cargo-hazardous",
            label: "Hazardous Substances",
            icon: <Radiation className="w-4 h-4" />,
            route: "CARGO_HAZARDOUS_SUBSTANCES_LIST",
          },
           {
            moduleId: "cargo-detention",
            label: "Detention Certificates",
            icon: <FileBadge2 className="w-4 h-4" />,
            route: "VECTOR_DETENTION_CERTS_LIST",
          },
             {
            moduleId: "cargo-tire-inspections",
            label: "Tyre Inspections",
            icon: <CircleDashed className="w-4 h-4" />,
            route: "CARGO_TIRE_INSPECTIONS_LIST",
          },
          {
            moduleId: "cargo-holding",
            label: "Holding Bay Logs",
            icon: <SearchIcon className="w-4 h-4" />,
            route: "CARGO_HOLDING_BAY_INSPECTIONS_LIST",
          },
          {
            moduleId: "cargo-sampling",
            label: "Sampling & Lab Submissions",
            icon: <FileBadge2 className="w-4 h-4" />,
            route: "CARGO_INSPECTION_OUTCOMES_LIST",
          },
        ],
      },
    ],
  },
  {
    id: "prem",
    label: "Environmental Health",
    icon: <Building2 className="w-4 h-4" />,
    sections: [
      {
        title: "Premises & Environmental Health",
        help: {
          title: "Environmental Health Playbook",
          processFlow: [
            "Prioritize premises using the risk scoring matrix and schedule inspection visits accordingly.",
            "Conduct environmental sampling and hygiene assessments, recording evidence and measurements.",
            "Submit laboratory samples for analysis and track corrective actions assigned to operators.",
            "Follow up on remediation timelines and update inspection statuses once compliance is verified.",
          ],
          items: [
            {
              label: "Premise Inspections",
              location: "Environmental Health SOP · Table 2 · Risk Scoring Matrix (pp. 26–27)",
              description: "Risk-based scoring criteria, documentation artefacts, and photographic evidence expectations during premises inspections.",
            },
            {
              label: "Water Quality Monitoring",
              location: "Water Quality Monitoring SOP · Section 5 · Chain-of-Custody Workflow (pp. 14–18)",
              description: "Sampling frequency tables, custody seal requirements, and laboratory submission protocols for potable water checks.",
            },
            {
              label: "Food Quality Monitoring",
              location: "Food Safety Inspection SOP · Section 4 · Food Handling Compliance Checklist (pp. 19–23)",
              description: "Critical control points, documentation, and sampling expectations for food service establishments.",
            },
            {
              label: "Sanitary/Hygiene Audits",
              location: "Sanitary Facilities SOP · Section 6 · Restroom & Wastewater Standards (pp. 28–31)",
              description: "Cleanliness benchmarks, disinfection routines, and non-compliance escalation steps for sanitary audits.",
            },
            // {
            //   label: "Indoor Air Quality Monitoring",
            //   location: "Indoor Air Quality BRD · Section 3.4 · Sensor Deployment & Logging (pp. 16–19)",
            //   description: "Device calibration intervals, sampling duration, and threshold triggers for air quality investigations.",
            // },
            // {
            //   label: "Noise Monitoring",
            //   location: "Environmental Noise SOP · Section 5 · Measurement & Reporting Protocol (pp. 22–26)",
            //   description: "Meter setup, permissible exposure limits, and reporting templates for noise assessments.",
            // },
          ],
        },
        links: [
          {
            moduleId: "environmental-premises",
            label: "Premise Inspections",
            icon: <SearchIcon className="w-4 h-4" />,
            route: "INSPECTIONS_PREMISES",
          },
          {
            moduleId: "environmental-food-water",
            label: "Food Quality Monitoring",
            icon: <IdCard className="w-4 h-4" />,
            route: "INSPECTIONS_FOOD_WATER",
          },
          {
            moduleId: "environmental-water-quality",
            label: "Water Quality Monitoring",
            icon: <Droplets className="w-4 h-4" />,
            route: "PREMISES_WATER_QUALITY_LIST",
          },
          {
            moduleId: "environmental-sanitary",
            label: "Sanitary/Hygiene Audits",
            icon: <Sparkles className="w-4 h-4" />,
            route: "INSPECTIONS_SANITARY",
          },
          // {
          //   moduleId: "environmental-indoor-air",
          //   label: "Indoor Air Quality Monitoring",
          //   icon: <Gauge className="w-4 h-4" />,
          //   route: "EH_INDOOR_AIR_QUALITY_LIST",
          // },
          // {
          //   moduleId: "environmental-noise",
          //   label: "Noise Monitoring",
          //   icon: <Activity className="w-4 h-4" />,
          //   route: "EH_NOISE_MONITORING_LIST",
          // },
          {
            moduleId: "environmental-health-education",
            label: "Health Education Activities",
            icon: <Users2 className="w-4 h-4" />,
            route: "EH_HEALTH_EDUCATION_LIST",
          },
          {
            moduleId: "environmental-trainings-meetings",
            label: "Trainings & Meetings",
            icon: <NotebookText className="w-4 h-4" />,
            route: "EH_TRAINING_MEETINGS_LIST",
          },
        ],
      },
      {
        title: "Licences & Certificates",
        help: {
          title: "Licensing & Certification SOPs",
          processFlow: [
            "Receive and verify applications against eligibility criteria for regulated premises and handlers.",
            "Conduct required inspections or training validations tied to the licence or certificate request.",
            "Issue approvals, update certificate registers, and communicate validity periods or conditions to applicants.",
          ],
          items: [
            {
              label: "Food Handler Certificates",
              location: "Food Handler Certification SOP · Section 3 · Training Verification & Issuance (pp. 9–14)",
              description: "Evidence requirements, renewal cadences, and revocation triggers for certified food handlers.",
            },
            {
              label: "Premise Licences",
              location: "Premises Licensing BRD · Section 5 · Approval Workflow & Decision Matrix (pp. 22–27)",
              description: "Risk scoring, maker-checker approvals, and licence term management for regulated premises.",
            },
          ],
        },
        links: [
          {
            moduleId: "food-handler-certificates",
            label: "Food Handler Certificates",
            icon: <ClipboardList className="w-4 h-4" />,
            route: "FOOD_HANDLER_CERTS",
          },
          {
            moduleId: "licences-premises",
            label: "Premise Licences",
            icon: <FileBadge2 className="w-4 h-4" />,
            route: "LICENCES_PREMISES",
          },
        ],
      },
      {
        title: "Vector Surveillance & Waste Management",
        help: {
          title: "Vector & Waste Operations Manual",
          processFlow: [
            "Plan surveillance routes and waste collection audits using recent vector indices and inspection history.",
            "Deploy field teams to capture trap counts, treatment activities, and waste handling observations.",
            "Submit lab samples or treatment records, analyse trends, and trigger remediation actions for hotspots.",
            "Update certificate registers, enforcement notices, and follow-up schedules based on surveillance outcomes.",
          ],
          items: [
            {
              label: "Vector Surveillance",
              location: "Vector Surveillance SOP · Section 4 · Trap Deployment & Specimen Handling (pp. 15–21)",
              description: "Trap placement guidance, species identification, and reporting templates for entomological surveillance.",
            },
            {
              label: "Ovitrap Monitoring",
              location: "Vector Surveillance SOP · Annex C · Ovitrap Servicing Schedule (pp. 32–34)",
              description: "Maintenance cadence, larval counts, and digital logging expectations for ovitrap networks.",
            },
            {
              label: "Residual Spraying",
              location: "Vector Control SOP · Section 5 · Residual Insecticide Application (pp. 24–28)",
              description: "Pre-spray risk assessments, mixing ratios, and post-treatment verification tasks.",
            },
            {
              label: "Used Tyres Inspection",
              location: "Waste & Tyre Management SOP · Section 3 · Used Tyre Surveillance Protocol (pp. 11–15)",
              description: "Inspection checklist, larval habitat scoring, and enforcement steps for tyre storage facilities.",
            },
            {
              label: "Rodent Inspections",
              location: "Rodent Control SOP · Section 2 · Inspection & Baiting Plans (pp. 8–13)",
              description: "Entry point mapping, bait station placement, and monitoring frequencies for rodent control.",
            },
            {
              label: "Waste Disposal Tracking",
              location: "Waste Management SOP · Section 4 · Collection & Manifest Reconciliation (pp. 19–24)",
              description: "Manifest verification, segregation requirements, and transporter compliance monitoring.",
            },
            {
              label: "Derating Certificates",
              location: "Vector Certification BRD · Section 3 · Deratting Certificate Lifecycle (pp. 14–17)",
              description: "Issuance criteria, validity periods, and renewal workflows for deratting certificates.",
            },
            {
              label: "Used Tyres Monthly Report",
              location: "Waste & Tyre Management SOP · Annex B · Monthly Reporting Templates (pp. 26–28)",
              description: "Required indicators, submission timelines, and document storage rules for tyre recycling reports.",
            },
          ],
        },
        links: [
          {
            moduleId: "vector-surveillance",
            label: "Vector Surveillance",
            icon: <Bug className="w-4 h-4" />,
            route: "VECTOR_MONITORING",
          },
          {
            moduleId: "vector-ovitraps",
            label: "Ovitrap Monitoring",
            icon: <Bug className="w-4 h-4" />,
            route: "VECTOR_OVITRAPS_LIST",
          },
          {
            moduleId: "vector-residual-spraying",
            label: "Residual Spraying",
            icon: <Sparkles className="w-4 h-4" />,
            route: "VECTOR_RESIDUAL_SPRAY_LIST",
          },
          // {
          //   moduleId: "vector-tyres",
          //   label: "Used Tyres Inspection",
          //   icon: <CircleDashed className="w-4 h-4" />,
          //   route: "VECTOR_TYRES_LIST",
          // },
          // {
          //   moduleId: "vector-rodents",
          //   label: "Rodent Inspections",
          //   icon: <Bug className="w-4 h-4" />,
          //   route: "VECTOR_RODENTS_LIST",
          // },
          {
            moduleId: "waste-logs",
            label: "Waste Disposal Tracking",
            icon: <Trash2 className="w-4 h-4" />,
            route: "WASTE_LOGS",
          },
          {
            moduleId: "vector-derating",
            label: "Derating Certificates",
            icon: <FileBadge2 className="w-4 h-4" />,
            route: "VECTOR_DERATING_CERTIFICATES_LIST",
          },
          // {
          //   moduleId: "cargo-detention",
          //   label: "Detention Certificates",
          //   icon: <FileBadge2 className="w-4 h-4" />,
          //   route: "VECTOR_DETENTION_CERTS_LIST",
          // },
        ],
      },
    ],
  },
  {
    id: "hr",
    label: "Human Remains",
    icon: <Landmark className="w-4 h-4" />,
    sections: [
      {
        title: "Human Remains Clearance",
        help: {
          title: "Human Remains Handling SOP",
          processFlow: [
            "Receive notification of human remains arrivals and validate transport and embalming documentation.",
            "Conduct physical verification, seal integrity checks, and coordinate with customs and security units.",
            "Approve release, issue clearance certificates, or escalate discrepancies for investigation and follow-up.",
          ],
          items: [
            {
              label: "Clearance Requests",
              location: "Human Remains SOP · Section 2 · Application Intake & Documentation Review (pp. 6–10)",
              description: "Required forms, documentary evidence, and maker-checker steps for processing clearance requests.",
            },
            {
              label: "Document Verification",
              location: "Human Remains SOP · Section 3 · Authenticity & Seal Verification (pp. 11–14)",
              description: "Verification routines for death certificates, embalming attestations, and transport permits.",
            },
            {
              label: "Clearance Certificates",
              location: "Human Remains SOP · Section 4 · Clearance Issuance & Release (pp. 15–18)",
              description: "Issuance criteria, record keeping, and release documentation for outgoing human remains.",
            },
          ],
        },
        links: [
          {
            moduleId: "hr-clearance",
            label: "Human Remains Clearance",
            icon: <ClipboardList className="w-4 h-4" />,
            route: "HR_CLEARANCE_LIST",
          },
          // {
          //   moduleId: "hr-certificates",
          //   label: "Clearance Certificates",
          //   icon: <FileBadge2 className="w-4 h-4" />,
          //   route: "HR_CERTIFICATES",
          // },
        ],
      },
    ],
  },
  {
    id: "more",
    label: "EPR",
    icon: <ClipboardList className="w-4 h-4" />,
    sections: [
      // {
      //   title: "Equipment & Asset Management",
      //   help: {
      //     title: "Equipment Logistics Manual",
      //     processFlow: [
      //       "Review stock levels and serviceability status for critical equipment and sampling kits.",
      //       "Assign equipment to field teams, capture calibration or maintenance logs, and track custody transfers.",
      //       "Reconcile returns, schedule servicing, and update asset registers to maintain operational readiness.",
      //     ],
      //     items: [
      //       {
      //         label: "Water sampling kits",
      //         location: "Equipment Management SOP · Section 3 · Water Kit Calibration & Issuance (pp. 12–16)",
      //         description: "Preparation steps, calibration checks, and issuance logs for potable water sampling kits.",
      //       },
      //       {
      //         label: "Food sampling kits",
      //         location: "Equipment Management SOP · Section 4 · Food Testing Kit Handling (pp. 17–21)",
      //         description: "Storage conditions, sterility controls, and deployment tracking for food sampling equipment.",
      //       },
      //       {
      //         label: "Rapid Diagnostic kits",
      //         location: "Diagnostic Logistics BRD · Section 5 · Rapid Test Kit Lifecycle (pp. 28–32)",
      //         description: "Lot registration, cold chain monitoring, and consumption reporting for rapid diagnostics.",
      //       },
      //       {
      //         label: "Radiation monitors",
      //         location: "Equipment Management SOP · Section 5 · Radiation Instrument Maintenance (pp. 22–26)",
      //         description: "Calibration schedules, battery management, and fault escalation for radiation meters.",
      //       },
      //       {
      //         label: "Monitoring badges for staff",
      //         location: "Occupational Monitoring SOP · Section 2 · Badge Issuance & Dosimetry Logging (pp. 8–13)",
      //         description: "Assignment records, wear-time tracking, and exposure reporting for staff monitoring badges.",
      //       },
      //     ],
      //   },
      //   links: [
      //     {
      //       moduleId: "equipment-water",
      //       label: "Water sampling kits",
      //       icon: <ClipboardList className="w-4 h-4" />,
      //       route: "EQUIPMENT_WATER_SAMPLING_LIST",
      //     },
      //     {
      //       moduleId: "equipment-food",
      //       label: "Food sampling kits",
      //       icon: <FileCheck2 className="w-4 h-4" />,
      //       route: "EQUIPMENT_FOOD_SAMPLING_LIST",
      //     },
      //     {
      //       moduleId: "equipment-rapid-diagnostics",
      //       label: "Rapid Diagnostic kits",
      //       icon: <HistoryIcon className="w-4 h-4" />,
      //       route: "EQUIPMENT_RAPID_DIAGNOSTIC_LIST",
      //     },
         
      //   ],
      // },
      {
        title: "Outbreak Response",
        help: {
          title: "Outbreak Response Handbook",
          processFlow: [
            "Review automated alerts and field reports to confirm the suspected outbreak trigger.",
            "Activate the incident management structure and assign investigation leads in PHMIS.",
            "Capture case findings, lab confirmations, and control measures within the incident logbook.",
            "Escalate situational updates to national EPR coordination and archive the response package.",
          ],
          items: [
            {
              label: "Incident Logbook",
              location: "Outbreak Incident Logbook BRD · Section 4 · Required Data Fields & Approvals (pp. 10–13)",
              description: "Defines mandatory metadata, maker-checker steps, and archival requirements for incident log entries.",
            },
            {
              label: "Case/Event Register",
              location: "Early Warning & Response BRD · Section 2 · Alert Generation & Review (pp. 9–12)",
              description: "Defines alert scoring, verification checks, and approval routing before mobilizing response teams.",
            },
            {
              label: "Contact Tracing",
              location: "Contact Tracing SOP · Section 3 · Field Investigation Procedures (pp. 13–18)",
              description: "Interview scripts, exposure categorization, and follow-up scheduling for contacts linked to the incident.",
            },
            {
              label: "Simulation Exercise Planning",
              location: "Outbreak Preparedness SOP · Section 5 · Exercise Documentation Standards (pp. 24–27)",
              description: "How to capture objectives, injects, and after-action findings for simulation and drill activities.",
            },
            {
              label: "Emergency Plans Registry",
              location: "Emergency Preparedness BRD · Section 4 · Plan Lifecycle Management (pp. 30–34)",
              description: "Requirements for registering, reviewing, and publishing approved emergency preparedness plans.",
            },
          ],
        },
        links: [
          {
            moduleId: "outbreak-alerts",
            label: "Case/Event Register",
            icon: <Bell className="w-4 h-4" />,
            route: "OUTBREAK_ALERTS",
          },
       
          {
            moduleId: "outbreak-drills",
            label: "Simulation Exercise Planning",
            icon: <ClipboardList className="w-4 h-4" />,
            route: "OUTBREAK_DRILLS",
          },
          {
            moduleId: "outbreak-logbook",
            label: "Incident Logbook",
            icon: <Book className="w-4 h-4" />,
            route: "OUTBREAK_LOGBOOK",
          },
          {
            moduleId: "outbreak-rumour-register",
            label: "Rumour Register",
            icon: <Megaphone className="w-4 h-4" />,
            route: "OUTBREAK_RUMOUR_REGISTER",
          },
          {
            moduleId: "outbreak-emergency-plans",
            label: "Emergency Plans Registry",
            icon: <BookOpen className="w-4 h-4" />,
            route: "OUTBREAK_EMERGENCY_PLANS",
          },
        ],
      },
    ],
  },
  {
    id: ADMIN_GROUP_ID,
    label: "Administration",
    icon: <Users2 className="w-4 h-4" />,
    sections: [
      {
        title: "Administration",
        links: [
          {
            moduleId: "admin-users",
            label: "User Accounts & Roles",
            icon: <Users2 className="w-4 h-4" />,
            route: "ADMIN_USERS",
          },
          {
            moduleId: "admin-role-matrix",
            label: "RBAC Matrix",
            icon: <ShieldCheck className="w-4 h-4" />,
            route: "ADMIN_ROLE_MATRIX",
          },
          {
            moduleId: "admin-poe-onboarding",
            label: "Port of Entry Onboarding",
            icon: <MapPinned className="w-4 h-4" />,
            route: "ADMIN_POE_CREATE",
          },
          // {
          //   moduleId: "admin-user-profile",
          //   label: "Officer Profile",
          //   icon: <IdCard className="w-4 h-4" />,
          //   route: "ADMIN_USER_PROFILE",
          // },
          {
            moduleId: "admin-hazardous-banned",
            label: "Hazardous Banned List",
            icon: <AlertTriangle className="w-4 h-4" />,
            route: "ADMIN_HAZARDOUS_BANNED_SUBSTANCES_LIST",
          },
          {
            moduleId: "admin-cargo-alert-configuration",
            label: "Cargo Alert Lookback",
            icon: <ShieldAlert className="w-4 h-4" />,
            route: "ADMIN_CARGO_ALERT_CONFIGURATION",
          },
          {
            moduleId: "sop-repository",
            label: "SOPs & Regulations",
            icon: <BookOpen className="w-4 h-4" />,
            route: "SOP_REPOSITORY_LIST",
          },
          {
            moduleId: "system-audit-logs",
            label: "System Audit Logs",
            icon: <HistoryIcon className="w-4 h-4" />,
            route: "AUDIT_LOGS_LIST",
          },
          {
            moduleId: "admin-document-requirements",
            label: "Document Requirement Definitions",
            icon: <FileText className="w-4 h-4" />,
            route: "ADMIN_DOC_REQUIREMENTS_LIST",
          },
          {
            moduleId: "admin-country-documents",
            label: "Country Documentation Mapping",
            icon: <ClipboardCheck className="w-4 h-4" />,
            route: "ADMIN_COUNTRY_DOC_LIST",
          },
          {
            moduleId: "platform-settings",
            label: "Settings",
            icon: <SlidersHorizontal className="w-4 h-4" />,
            route: "SETTINGS",
          },
        ],
      },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    icon: <BarChart3 className="w-4 h-4" />,
    sections: [
      // {
      //   title: "Reports Center",
      //   links: [
      //     {
      //       moduleId: "reports-center",
      //       label: "Reports",
      //       icon: <NotebookText className="w-4 h-4" />,
      //       route: "REPORTS_CENTER_LIST",
      //     },
      //   ],
      // },
      {
        title: "Reports Library",
        links: [
          // {
          //   moduleId: "reports-library",
          //   label: "Reports Library",
          //   icon: <Book className="w-4 h-4" />,
          //   route: "REPORTS_DASHBOARDS",
          // },
          {
            moduleId: "reports-library",
            label: "Daily Audit Report",
            icon: <ClipboardList className="w-4 h-4" />,
            route: "REPORTS_DAILY_AUDIT",
          },
          {
            moduleId: "cargo-smuggled-goods-report",
            label: "Smuggled Goods Report",
            icon: <FileSearch className="w-4 h-4" />,
            route: "SMUGGLED_GOODS_LIST",
          },
          {
            moduleId: "reports-capture-summary",
            label: "User Capture Summary",
            icon: <Users2 className="w-4 h-4" />,
            route: "REPORTS_CAPTURE_SUMMARY",
          },
        ],
      },
      {
        title: "Report Builder",
        links: [
          {
            moduleId: "reports-library",
            label: "Custom Report Builder",
            icon: <Sparkles className="w-4 h-4" />,
            route: "REPORTS_CUSTOM",
          },
        ],
      },
      {
        title: "Operational Reports",
        links: [
          {
            moduleId: "reports-conveyance-inspection",
            label: "Conveyance Inspection",
            icon: <ClipboardCheck className="w-4 h-4" />,
            route: "REPORTS_CONVEYANCE_INSPECTION",
          },
          {
            moduleId: "reports-general-declarations",
            label: "General Declarations",
            icon: <NotebookText className="w-4 h-4" />,
            route: "REPORTS_GENERAL_DECLARATIONS",
          },
          {
            moduleId: "reports-cargo-summary",
            label: "Cargo Summary",
            icon: <Package className="w-4 h-4" />,
            route: "REPORTS_CARGO_SUMMARY",
          },
          {
            moduleId: "reports-cargo-clearance",
            label: "Cargo Clearance",
            icon: <Package className="w-4 h-4" />,
            route: "REPORTS_CARGO_CLEARANCE",
          },
          {
            moduleId: "reports-cargo-hazardous-register",
            label: "Hazardous Cargo Register",
            icon: <AlertTriangle className="w-4 h-4" />,
            route: "REPORTS_CARGO_HAZARDOUS",
          },
          {
            moduleId: "reports-cargo-condemnation",
            label: "Condemnation Report",
            icon: <Trash2 className="w-4 h-4" />,
            route: "REPORTS_CARGO_CONDEMNATION",
          },
          {
            moduleId: "reports-human-remains-summary",
            label: "Human Remains Clearance",
            icon: <Skull className="w-4 h-4" />,
            route: "REPORTS_HUMAN_REMAINS_SUMMARY",
          },
          {
            moduleId: "reports-traveller-screening",
            label: "Traveller Screening",
            icon: <Stethoscope className="w-4 h-4" />,
            route: "REPORTS_TRAVELLER_SCREENING",
          },
          {
            moduleId: "reports-traveller-disease",
            label: "Disease & Condition",
            icon: <Activity className="w-4 h-4" />,
            route: "REPORTS_TRAVELLER_DISEASE_CONDITIONS",
          },
          {
            moduleId: "reports-vector-used-tyres",
            label: "Used Tyres Monthly Report",
            icon: <BarChart3 className="w-4 h-4" />,
            route: "VECTOR_TYRES_REPORT_MONTHLY",
          },
          {
            moduleId: "reports-waste-management",
            label: "Waste Management Report",
            icon: <Trash2 className="w-4 h-4" />,
            route: "REPORTS_WASTE_MANAGEMENT",
          },
        ],
      },
      {
        title: "Environmental Monitoring",
        links: [
          {
            moduleId: "reports-environmental-premises",
            label: "Premises Inspection",
            icon: <Building2 className="w-4 h-4" />,
            route: "REPORTS_ENVIRONMENTAL_PREMISES",
          },
          {
            moduleId: "reports-environmental-scorecard",
            label: "Sanitary Scorecard",
            icon: <ClipboardList className="w-4 h-4" />,
            route: "REPORTS_ENVIRONMENTAL_SANITARY_SCORECARD",
          },
          {
            moduleId: "reports-water-quality",
            label: "Water Quality",
            icon: <Droplets className="w-4 h-4" />,
            route: "REPORTS_WATER_QUALITY",
          },
          {
            moduleId: "reports-noise-quality",
            label: "Noise Quality",
            icon: <Gauge className="w-4 h-4" />,
            route: "REPORTS_NOISE_QUALITY",
          },
          {
            moduleId: "reports-air-quality",
            label: "Air Quality",
            icon: <ThermometerSun className="w-4 h-4" />,
            route: "REPORTS_AIR_QUALITY",
          },
          {
            moduleId: "reports-vector-surveillance",
            label: "Vector Surveillance",
            icon: <Bug className="w-4 h-4" />,
            route: "REPORTS_VECTOR_SURVEILLANCE",
          },
          {
            moduleId: "reports-vector-used-tyres",
            label: "Used Tyre Surveillance",
            icon: <CircleDashed className="w-4 h-4" />,
            route: "REPORTS_VECTOR_USED_TYRES",
          },
          {
            moduleId: "reports-vector-control",
            label: "Vector Control",
            icon: <Bug className="w-4 h-4" />,
            route: "REPORTS_VECTOR_CONTROL",
          },
        ],
      },
      {
        title: "Platform Oversight",
        links: [
          {
            moduleId: "reports-audit-trail",
            label: "Audit Trail Report",
            icon: <FileText className="w-4 h-4" />,
            route: "REPORTS_AUDIT_TRAIL",
          },
        ],
      },
      {
        title: "Domain dashboards",
        links: [
          {
            moduleId: "reports-conveyance-inspection",
            label: "Conveyance Inspection",
            icon: <ClipboardCheck className="w-4 h-4" />,
            route: "REPORTS_CONVEYANCE_INSPECTION",
          },
          {
            moduleId: "reports-cargo-summary",
            label: "Cargo Summary",
            icon: <Package className="w-4 h-4" />,
            route: "REPORTS_CARGO_SUMMARY",
          },
          {
            moduleId: "reports-cargo-hazardous-register",
            label: "Hazardous Cargo Register",
            icon: <AlertTriangle className="w-4 h-4" />,
            route: "REPORTS_CARGO_HAZARDOUS",
          },
          {
            moduleId: "reports-cargo-condemnation",
            label: "Condemnation Report",
            icon: <Trash2 className="w-4 h-4" />,
            route: "REPORTS_CARGO_CONDEMNATION",
          },
          {
            moduleId: "reports-human-remains-summary",
            label: "Human Remains Report",
            icon: <Skull className="w-4 h-4" />,
            route: "REPORTS_HUMAN_REMAINS_SUMMARY",
          },
          {
            moduleId: "reports-waste-management",
            label: "Waste Management Report",
            icon: <Trash2 className="w-4 h-4" />,
            route: "REPORTS_WASTE_MANAGEMENT",
          },
        ],
      },
      // {
      //   title: "Domain dashboards",
      //   links: [
      //     {
      //       moduleId: "dashboards-domain",
      //       label: "Port Operations",
      //       icon: <BarChart3 className="w-4 h-4" />,
      //       route: "DASHBOARDS_PORT_OPERATIONS",
      //     },
      //     {
      //       moduleId: "dashboards-domain",
      //       label: "Traveller Health",
      //       icon: <Stethoscope className="w-4 h-4" />,
      //       route: "DASHBOARDS_TRAVELLER_HEALTH",
      //     },
      //     {
      //       moduleId: "dashboards-domain",
      //       label: "Conveyance Oversight",
      //       icon: <Plane className="w-4 h-4" />,
      //       route: "DASHBOARDS_CONVEYANCE_OVERSIGHT",
      //     },
      //     {
      //       moduleId: "dashboards-domain",
      //       label: "Cargo & Holding Bay",
      //       icon: <Package className="w-4 h-4" />,
      //       route: "DASHBOARDS_CARGO_HOLDING_BAY",
      //     },
      //     {
      //       moduleId: "dashboards-domain",
      //       label: "Human Remains",
      //       icon: <Skull className="w-4 h-4" />,
      //       route: "DASHBOARDS_HUMAN_REMAINS",
      //     },
      //     {
      //       moduleId: "dashboards-domain",
      //       label: "Vector Surveillance",
      //       icon: <Bug className="w-4 h-4" />,
      //       route: "DASHBOARDS_VECTOR_SURVEILLANCE",
      //     },
      //     {
      //       moduleId: "dashboards-domain",
      //       label: "Environmental Health",
      //       icon: <Building2 className="w-4 h-4" />,
      //       route: "DASHBOARDS_ENVIRONMENTAL_HEALTH",
      //     },
      //     {
      //       moduleId: "dashboards-domain",
      //       label: "Outbreaks & EPR",
      //       icon: <BellRing className="w-4 h-4" />,
      //       route: "DASHBOARDS_OUTBREAKS_EPR",
      //     },
      //   ],
      // },
      // {
      //   title: "Finance & Fees",
      //   links: [
      //     {
      //       moduleId: "dashboards-finance",
      //       label: "Revenue & Collections",
      //       icon: <Banknote className="w-4 h-4" />,
      //       route: "DASHBOARDS_FINANCE_REVENUE",
      //     },
      //     {
      //       moduleId: "dashboards-finance",
      //       label: "ZIMRA Integration",
      //       icon: <ShieldCheck className="w-4 h-4" />,
      //       route: "DASHBOARDS_FINANCE_ZIMRA",
      //     },
      //     {
      //       moduleId: "dashboards-finance",
      //       label: "Reconciliation Variances",
      //       icon: <Receipt className="w-4 h-4" />,
      //       route: "DASHBOARDS_FINANCE_RECON",
      //     },
      //   ],
      // },
      // {
      //   title: "Admin, Compliance & QA",
      //   links: [
      //     {
      //       moduleId: "dashboards-admin",
      //       label: "Audit Trails",
      //       icon: <Activity className="w-4 h-4" />,
      //       route: "DASHBOARDS_ADMIN_AUDIT",
      //     },
      //     {
      //       moduleId: "dashboards-admin",
      //       label: "Compliance Status",
      //       icon: <ShieldAlert className="w-4 h-4" />,
      //       route: "DASHBOARDS_ADMIN_COMPLIANCE",
      //     },
      //   ],
      // },
      // {
      //   title: "Operational Dashboards",
      //   links: [
      //     {
      //       moduleId: "dashboards-operational",
      //       label: "Port Performance (Daily)",
      //       icon: <Gauge className="w-4 h-4" />,
      //       route: "DASHBOARDS_OPS_PORT_DAILY",
      //     },
      //     {
      //       moduleId: "dashboards-operational",
      //       label: "Compliance Dashboard",
      //       icon: <TrafficCone className="w-4 h-4" />,
      //       route: "DASHBOARDS_OPS_COMPLIANCE",
      //     },
      //     {
      //       moduleId: "dashboards-operational",
      //       label: "Risk & Alerts",
      //       icon: <AlertTriangle className="w-4 h-4" />,
      //       route: "DASHBOARDS_OPS_RISK",
      //     },
      //     {
      //       moduleId: "dashboards-operational",
      //       label: "Port Ops",
      //       icon: <AlertTriangle className="w-4 h-4" />,
      //       route: "REPORTS_PORT_OPS",
      //     },
      //     {
      //       moduleId: "dashboards-operational",
      //       label: "Vector Surveillance",
      //       icon: <AlertTriangle className="w-4 h-4" />,
      //       route: "REPORTS_VECTOR",
      //     },
      //   ],
      // },
      // {
      //   title: "Standard Exports",
      //   links: [
      //     {
      //       moduleId: "reports-exports",
      //       label: "Monthly Used Tyres Monitoring",
      //       icon: <Download className="w-4 h-4" />,
      //       route: "REPORTS_EXPORTS_TYRES",
      //     },
      //     {
      //       moduleId: "reports-exports",
      //       label: "General Declaration Register",
      //       icon: <Download className="w-4 h-4" />,
      //       route: "REPORTS_EXPORTS_GD",
      //     },
      //     {
      //       moduleId: "reports-exports",
      //       label: "Surveillance Register",
      //       icon: <Download className="w-4 h-4" />,
      //       route: "REPORTS_EXPORTS_SURVEILLANCE",
      //     },
      //     {
      //       moduleId: "reports-exports",
      //       label: "Sampling Chain-of-Custody",
      //       icon: <Download className="w-4 h-4" />,
      //       route: "REPORTS_EXPORTS_COC",
      //     },
      //     {
      //       moduleId: "reports-exports",
      //       label: "Licences & Certificates Expiry",
      //       icon: <Download className="w-4 h-4" />,
      //       route: "REPORTS_EXPORTS_EXPIRY",
      //     },
      //   ],
      // },
    ],
  },
  {
    id: "workflows",
    label: "Workflows",
    icon: <GitPullRequest className="w-4 h-4" />,
    type: "link",
    link: {
      moduleId: "dashboards-admin",
      route: "DASHBOARDS_ADMIN_MAKER_CHECKER",
    },
  },
]

function useOutside(ref: React.RefObject<HTMLElement | null>, onOutside: () => void) {
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) onOutside()
    }
    document.addEventListener("click", handle)
    return () => document.removeEventListener("click", handle)
  }, [ref, onOutside])
}

type PHMISMenubarProps = {
  roleCode?: string | null
}

export default function PHMISMenubar({ roleCode }: PHMISMenubarProps) {
  const loc = useLocation()
  const { canUseModule } = useFacilityAccess()
  const [open, setOpen] = useState<string | null>(null)
  const [panelPos, setPanelPos] = useState<{ left: number; top: number; width: number }>({ left: 0, top: 0, width: 640 })
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileGroup, setMobileGroup] = useState<string | null>(null)
  const [qrScannerOpen, setQrScannerOpen] = useState(false)
  //const barRef = useRef<HTMLElement | null>(null)
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  
  const wrapRef = useRef<HTMLDivElement | null>(null)
  useOutside(wrapRef, () => setOpen(null))

  const allowAdministrationMenu = true

  const groups = useMemo<Group[]>(() => {
    const filteredGroups = MENUBAR_GROUPS.filter((group) => allowAdministrationMenu || group.id !== ADMIN_GROUP_ID)

    const builtGroups = filteredGroups.map<Group | null>((group) => {
      const bypassModuleGate = allowAdministrationMenu && group.id === ADMIN_GROUP_ID
      if (group.type === "link") {
        const { link } = group
        if (!bypassModuleGate && !canUseModule(link.moduleId)) return null
        const module = getModuleDefinition(link.moduleId)
        if (!module || module.routes.length === 0) return null
        const routeKey: RouteKey | undefined = link.route ?? module.routes[0]
        if (!routeKey) return null
        const pathValue = PATHS[routeKey]
        if (!pathValue) return null
        return {
          id: group.id,
          label: group.label,
          icon: group.icon,
          badge: group.badge,
          type: "link",
          link: {
            label: link.label ?? module.label,
            to: pathValue,
            icon: link.icon,
          },
        }
      }

      const sections = group.sections
        .map<Section | null>((section) => {
          const links = section.links
            .map<LinkItem | null>((link) => {
              if (!bypassModuleGate && !canUseModule(link.moduleId)) return null
              const module = getModuleDefinition(link.moduleId)
              if (!module || module.routes.length === 0) return null
              const routeKey: RouteKey | undefined = link.route ?? module.routes[0]
              if (!routeKey) return null
              const pathValue = PATHS[routeKey]
              if (!pathValue) return null
              return {
                label: link.label ?? module.label,
                to: pathValue,
                icon: link.icon,
              }
            })
            .filter((link): link is LinkItem => link !== null)
          if (links.length === 0) return null
          return { title: section.title, links, help: section.help, layout: section.layout }
        })
        .filter((section): section is Section => section !== null)
      if (sections.length === 0) return null
      return {
        id: group.id,
        label: group.label,
        icon: group.icon,
        badge: group.badge,
        type: "dropdown",
        sections,
      }
    })

    return builtGroups.filter((group): group is Group => group !== null)
  }, [allowAdministrationMenu, canUseModule])


  useEffect(() => {
    setOpen(null)
    setMobileOpen(false)
    setQrScannerOpen(false)
    Swal.close()
  }, [loc.pathname])

  const isPathMatch = useCallback((currentPath: string, targetPath: string) => {
    if (!currentPath || !targetPath) return false
    const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const pattern = targetPath
      .split("/")
      .map((segment) => (segment.startsWith(":") ? "[^/]+" : escapeRegex(segment)))
      .join("/")
    const regex = new RegExp(`^${pattern}(?:/.*)?$`)
    return regex.test(currentPath)
  }, [])

  const activeGroupId = useMemo(() => {
    const path = loc.pathname
    for (const group of groups) {
      if (group.type === "link") {
        if (isPathMatch(path, group.link.to)) return group.id
        continue
      }
      for (const section of group.sections) {
        for (const link of section.links) {
          if (isPathMatch(path, link.to)) {
            return group.id
          }
        }
      }
    }
    return null
  }, [groups, isPathMatch, loc.pathname])

  function openPanel(id: string) {
    const btn = btnRefs.current[id]
    if (!btn) return

    const dropdown = groups.find(
      (group): group is DropdownGroup => group.type === "dropdown" && group.id === id
    )
    const sectionCount = dropdown?.sections.length ?? 1
    const columnCount = Math.min(3, Math.max(1, sectionCount))

    const columnWidth = 288
    const gapWidth = 16
    const panelPadding = 32

    const targetWidth =
      columnCount * columnWidth + (columnCount - 1) * gapWidth + panelPadding

    const rect = btn.getBoundingClientRect()
    const vw = window.innerWidth
    const width = Math.min(Math.floor(vw * 0.9), targetWidth)
    const left = Math.max(8, Math.min(rect.left, vw - width - 8))
    const top = rect.bottom + 8

    setPanelPos({ left, top, width })
    setOpen(id)
  }

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : ""
  }, [mobileOpen])

  useEffect(() => {
    function onResize() {
      if (open) openPanel(open)
    }
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [open])

  const handleSectionHelp = useCallback((sectionTitle: string, help: SectionHelp) => {
    const processFlowSteps = Array.isArray(help.processFlow)
      ? help.processFlow
      : help.processFlow
        ? [help.processFlow]
        : []
    if (!processFlowSteps.length && !help.items.length) return

    const contentSections: string[] = []

    if (processFlowSteps.length) {
      contentSections.push(
        [
          "<div>",
          '<div class=\"text-xs font-medium uppercase tracking-wide text-slate-500\">Process flow</div>',
          '<ol class=\"mt-2 list-decimal pl-5 space-y-1 text-sm text-slate-600\">',
          ...processFlowSteps.map((step) => `<li>${step}</li>`),
          "</ol>",
          "</div>",
        ].join("")
      )
    }

    if (help.items.length) {
      contentSections.push(
        help.items
          .map((item) => {
            const location = `<div class=\"text-xs font-medium uppercase tracking-wide text-slate-500\">${item.location}</div>`
            const description = item.description
              ? `<div class=\"mt-1 text-sm text-slate-600\">${item.description}</div>`
              : ""
            return [
              "<div>",
              `<div class=\"font-semibold text-slate-700\">${item.label}</div>`,
              location,
              description,
              "</div>",
            ].join("")
          })
          .join("")
      )
    }

    const html = [`<div class=\"text-left space-y-4\">`, ...contentSections, "</div>"].join("")

 
    void Swal.fire({
      title: help.title ?? sectionTitle,
      html,
      confirmButtonText: "Close",
      focusConfirm: true,
      showCloseButton: true,
      width: "32rem",
    })
  }, [])

  const openSections = open
    ? groups.find((g): g is DropdownGroup => g.type === "dropdown" && g.id === open)?.sections ?? []
    : []
  const sectionGridClassName = [
    "grid gap-4",
    ...(openSections.length >= 2 ? ["md:grid-cols-2"] : []),
    ...(openSections.length >= 3 ? ["lg:grid-cols-3"] : []),
  ].join(" ")

  return (
    <div ref={wrapRef} className="relative border-b border-slate-200 bg-white">
      <div className="flex items-center gap-2 px-4 py-2">
        <button
          className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-slate-200"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        <div className="hidden md:flex flex-1 gap-2 overflow-x-auto">
          <NavLink
            to={PATHS.ROOT}
            className={({ isActive }) =>
              [
                "inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold border transition",
                isActive ? "bg-indigo-50 border-indigo-200 text-indigo-800" : "border-transparent hover:bg-slate-50",
              ].join(" ")
            }
          >
            <HomeIcon className="w-4 h-4" />
            Home
          </NavLink>

          {groups.map((g) =>
            g.type === "link" ? (
              <NavLink
                key={g.id}
                to={g.link.to}
                className={({ isActive }) =>
                  [
                     "inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold border transition shadow-sm",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
                    isActive || activeGroupId === g.id
                      ? "bg-indigo-700 border-indigo-700 text-white shadow"
                      : "bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-500",
 
                  ].join(" ")
                }
              >
                {g.icon}
                {g.label}
                {g.badge ? (
                  <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[18px] px-1.5 rounded-full text-[10px] font-bold bg-rose-600 text-white">
                    {g.badge}
                  </span>
                ) : null}
              </NavLink>
            ) : (
              <div key={g.id} className="relative">
                <button
                  ref={(el) => {
                    btnRefs.current[g.id] = el
                  }}
                  onClick={() => (open === g.id ? setOpen(null) : openPanel(g.id))}
                  className={[
                    "inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold border transition",
                    activeGroupId === g.id
                      ? "border-indigo-600 bg-indigo-50 text-indigo-800 shadow-[0_2px_10px_rgba(79,70,229,0.15)]"
                      : "border-transparent hover:bg-slate-50",
                  ].join(" ")}
                  aria-expanded={open === g.id}
                >
                  {g.icon}
                  {g.label}
                  <ChevronDown className={`w-4 h-4 transition-transform ${open === g.id ? "rotate-180" : ""}`} />
                  {g.badge ? (
                    <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[18px] px-1.5 rounded-full text-[10px] font-bold bg-rose-600 text-white">
                      {g.badge}
                    </span>
                  ) : null}
                </button>
              </div>
            )
          )}

          <button
            type="button"
            onClick={() => setQrScannerOpen(true)}
            className="inline-flex items-center gap-2 rounded-md border bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            <QrCode className="h-4 w-4" />
            Scan QR
          </button>
        </div>
      </div>
      {open && (
        <>
          <div className="hidden md:block fixed inset-0 z-[60]" onClick={() => setOpen(null)} />
          <div
            className="hidden md:block fixed z-[70] bg-white shadow-2xl border border-slate-200 rounded-xl p-4 overflow-y-auto overscroll-contain"
            style={{ left: panelPos.left, top: panelPos.top, width: panelPos.width, maxHeight: "calc(100vh - 96px)" }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={sectionGridClassName}>
              {openSections.map((s) => (
                <div key={s.title} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{s.title}</h5>
                    {s.help ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (s.help) handleSectionHelp(s.title, s.help)
                        }}
                        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:text-indigo-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                        aria-label={`Open help for ${s.title}`}
                      >
                        <CircleHelp className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                  <div
                    className={
                      s.layout?.columns === 2
                        ? "grid gap-1 grid-cols-2"
                        : s.layout?.columns === 3
                        ? "grid gap-1 grid-cols-3"
                        : "flex flex-col"
                    }
                  >
                    {(() => {
                      if (!s.layout) {
                        return s.links.map((l) => (
                          <NavLink
                            key={l.to + l.label}
                            to={l.to}
                            onClick={() => setOpen(null)}
                            className="flex items-center gap-2 px-2 py-2 rounded-md text-sm hover:bg-slate-50"
                          >
                            {l.icon}
                            <span>{l.label}</span>
                          </NavLink>
                        ))
                      }

                      const columnCount = Math.max(1, s.layout.columns)
                      const columnLabels = s.layout.columnLabels ?? []
                      const assigned = new Set<string>()
                      const columns = Array.from({ length: columnCount }, (_, index) => {
                        const labels = columnLabels[index] ?? []
                        const columnLinks = labels
                          .map((label) => {
                            const link = s.links.find((l) => l.label === label)
                            if (link && !assigned.has(link.label)) {
                              assigned.add(link.label)
                              return link
                            }
                            return null
                          })
                          .filter((link): link is LinkItem => link !== null)
                        return columnLinks
                      })

                      const leftovers = s.links.filter((link) => !assigned.has(link.label))
                      if (leftovers.length) {
                        columns[0] = [...leftovers, ...(columns[0] ?? [])]
                      }

                      return columns.map((columnLinks, columnIndex) => (
                        <div key={`column-${columnIndex}`} className="flex flex-col">
                          {columnLinks.map((l) => (
                            <NavLink
                              key={l.to + l.label}
                              to={l.to}
                              onClick={() => setOpen(null)}
                              className="flex items-center gap-2 px-2 py-2 rounded-md text-sm hover:bg-slate-50"
                            >
                              {l.icon}
                              <span>{l.label}</span>
                            </NavLink>
                          ))}
                        </div>
                      ))
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {mobileOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <div className="fixed inset-y-0 left-0 z-[70] w-[88vw] max-w-sm bg-white shadow-2xl border-r border-slate-200 flex flex-col">
            <div className="flex items-center gap-2 p-3 border-b">
              <button
                className="h-9 w-9 inline-flex items-center justify-center rounded-md border"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="ml-2 font-semibold">Menu</div>
            </div>
            <div className="p-3 border-b">
              <div className="flex items-center gap-2 border rounded-md px-2 py-1.5 bg-slate-50">
                <SearchIcon className="w-4 h-4 text-slate-500" />
                <input className="bg-transparent outline-none text-sm w-full" placeholder="Search modules & actions" />
              </div>
            </div>
            <div className="px-2 pb-8 overflow-y-auto">
              <NavLink
                to={PATHS.ROOT}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold hover:bg-slate-50"
              >
                <HomeIcon className="w-4 h-4" />
                Home
              </NavLink>

              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false)
                  setQrScannerOpen(true)
                }}
                className="mt-2 inline-flex w-full items-center gap-2 rounded-md border bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              >
                <QrCode className="h-4 w-4" />
                Scan QR
              </button>

              {groups.map((g) => (
                <div key={g.id} className="mt-1">
                  {g.type === "link" ? (
                    <NavLink
                      to={g.link.to}
                      onClick={() => setMobileOpen(false)}
                       className={({ isActive }) =>
                        [
                          "w-full inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition shadow-sm",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
                          isActive || activeGroupId === g.id
                            ? "bg-indigo-700 text-white shadow"
                            : "bg-indigo-600 text-white hover:bg-indigo-500",
                        ].join(" ")
                      }
 
                    >
                      {g.icon}
                      {g.label}
                      {g.badge ? (
                        <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[18px] px-1.5 rounded-full text-[10px] font-bold bg-rose-600 text-white">
                          {g.badge}
                        </span>
                      ) : null}
                    </NavLink>
                  ) : (
                    <>
                      <button
                        onClick={() => setMobileGroup(mobileGroup === g.id ? null : g.id)}
                        className={[
                          "w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-semibold transition",
                          activeGroupId === g.id ? "bg-indigo-50 text-indigo-800 shadow-inner" : "hover:bg-slate-50",
                        ].join(" ")}
                      >
                        <span className="inline-flex items-center gap-2">
                          {g.icon}
                          {g.label}
                          {g.badge ? (
                            <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[18px] px-1.5 rounded-full text-[10px] font-bold bg-rose-600 text-white">
                              {g.badge}
                            </span>
                          ) : null}
                        </span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${mobileGroup === g.id ? "rotate-180" : ""}`} />
                      </button>

                      {mobileGroup === g.id && (
                        <div className="pl-3">
                          {g.sections.map((s) => (
                            <div key={s.title} className="mt-2">
                              <div className="flex items-center justify-between px-3">
                                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{s.title}</div>
                                {s.help ? (
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.preventDefault()
                                      event.stopPropagation()
                                      if (s.help) handleSectionHelp(s.title, s.help)
                                    }}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:text-indigo-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                                    aria-label={`Open help for ${s.title}`}
                                  >
                                    <CircleHelp className="h-4 w-4" />
                                  </button>
                                ) : null}
                              </div>
                              <div className="mt-1 flex flex-col">
                                {s.links.map((l) => (
                                  <NavLink
                                    key={l.to + l.label}
                                    to={l.to}
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-slate-50"
                                  >
                                    {l.icon}
                                    <span>{l.label}</span>
                                  </NavLink>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <MenubarQrScannerModal open={qrScannerOpen} onClose={() => setQrScannerOpen(false)} />
    </div>
  )
}
